/**
 * THE PROJECT GUIDE — "Set up & run this project" — connected-flow increment #4.
 *
 * WHY IT EXISTS. Vinnie, 16 September 2026: *"there is no order to which document
 * you do first… there is nothing that guides you at all."* The app captured good
 * data but left people hunting — no obvious first step, no sense of what feeds what.
 * This is the connective tissue: a calm, dismissible checklist of the real stages a
 * project runs through — charter, scope, requirements, plan & phases, sprints,
 * sign-off, release — each showing where it stands and linking straight to where the
 * work is done. Nothing new to learn; it mirrors the agile lifecycle the app already
 * runs, so it reads as a map of what is already here rather than a new feature.
 *
 * IT IS DERIVED, NOT TRACKED. Every stage's state is worked out from the same data
 * the rest of the app shows — the documents, the About fields, the backlog, the
 * phases, the sprints, the releases — so a step goes green the moment the real work
 * is done, with nothing to tick and nothing that can drift from the truth.
 *
 * DISMISSIBLE, PER VIEWER. A mature project does not need the map open; Hide folds it
 * to a single line that brings it back, remembered in this browser (like the board
 * choice) so one person's dismissal is not imposed on the team.
 *
 * WHERE IT LIVES, AND WHAT IS STILL TO COME. It sits at the top of the Plan — the
 * project's home. Board-only teams never open the Plan, so a condensed Board strip is
 * the planned next slice (#4 continued); this is the Plan surface.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import { projectStages, runStageTarget, type StageState } from '../project-guide';
import GuidanceRow from './GuidanceRow';

const hiddenKey = (projectId: string) => `fd.guide.hidden.${projectId}`;
const readHidden = (projectId: string): boolean => {
  try {
    // DEFAULT CLOSED (Vinnie, 13 Oct 2026): the setup guide starts hidden — a
    // compact "Show guide" bar — and only opens when the viewer chooses to. The
    // choice is remembered per viewer: 'open' shows it; anything else keeps it
    // closed, which covers a first visit (no value) and the legacy '1' that used
    // to mean hidden.
    return localStorage.getItem(hiddenKey(projectId)) !== 'open';
  } catch {
    return true;
  }
};

export default function ProjectGuide() {
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const project = projects.find((p) => p.id === projectId);
  const documents = useApp((s) => s.documents);
  const phases = useApp((s) => s.phases);
  const issues = useApp((s) => s.issues);
  const sprints = useApp((s) => s.sprints);
  const builds = useApp((s) => s.builds);
  const role = useApp((s) => s.shownRole);
  const drillTo = useApp((s) => s.drillTo);
  const draftDocument = useApp((s) => s.draftDocument);

  const mayPlan = can(role, 'editSettings');

  const [hidden, setHidden] = useState(() => readHidden(projectId));
  const [problem, setProblem] = useState('');

  const setHiddenPersist = (v: boolean) => {
    setHidden(v);
    try {
      // Persist BOTH choices now that closed is the default: 'open' so a viewer
      // who opens the guide keeps it open next time; 'closed' when they hide it.
      localStorage.setItem(hiddenKey(projectId), v ? 'closed' : 'open');
    } catch {
      /* Private browsing — the guide simply returns to its default (closed) next load. */
    }
  };

  const stages = useMemo(
    () => projectStages({ project, documents, phases, issues, sprints, builds, mayPlan }),
    [documents, project, issues, phases, sprints, builds, mayPlan],
  );

  const go = (stage: (typeof stages)[number]) =>
    runStageTarget(stage.target, drillTo, draftDocument, setProblem);

  if (!project) return null;

  const done = stages.filter((s) => s.state === 'done').length;

  const mark = (state: StageState) =>
    state === 'done' ? (
      <span className="pg-mark pg-done" aria-label="Done">
        ✓
      </span>
    ) : state === 'now' ? (
      <span className="pg-mark pg-now" aria-label="Needs you now">
        <span className="pg-dot" />
      </span>
    ) : (
      <span className="pg-mark pg-todo" aria-label="Still to come" />
    );

  return (
    <GuidanceRow
      title="How to set up & run this project"
      peek="The stages, and where each stands"
      count={`${done} / ${stages.length}`}
      open={!hidden}
      onToggle={() => setHiddenPersist(hidden ? false : true)}
    >
      <p className="pg-sub">
        Each step links straight to where you do it. You never have to go looking — and each one feeds
        the next.
      </p>

      {problem && <p className="pg-problem">{problem}</p>}

      {stages.map((s) => (
        <div className="pg-stage" key={s.key}>
          {mark(s.state)}
          <div className="pg-body">
            <div className="pg-line">
              <span className="pg-name">{s.name}</span>
              {s.feeds && <span className="pg-feeds">{s.feeds}</span>}
              {s.actionLabel ? (
                <button type="button" className="pg-go" onClick={() => go(s)}>
                  {s.actionLabel}
                </button>
              ) : (
                s.status && <span className="pg-status">{s.status}</span>
              )}
            </div>
            <p className="pg-what">{s.what}</p>
          </div>
        </div>
      ))}
    </GuidanceRow>
  );
}
